import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { LegalFooter, LEGAL_LINKS } from "@/components/LegalFooter";
import { euro } from "@/lib/format";
import { ArrowLeft, ShieldCheck, Lock, CreditCard, Mail } from "lucide-react";

const useInfo = () => {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api.get("/public/info").then(({ data }) => setInfo(data)).catch(() => setError(true));
  }, []);
  return { info, error };
};

const Shell = ({ title, subtitle, children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src="/photopay-logo.jpeg" alt="MIHOD PAY" className="h-8 w-auto rounded-md" />
            <span className="font-display text-sm">MIHOD PAY</span>
          </Link>
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => navigate(-1)} data-testid="legal-back"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
        </div>
        <nav className="max-w-3xl mx-auto px-4 pb-2 flex gap-1 overflow-x-auto text-xs" data-testid="legal-nav">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={`px-2.5 py-1 rounded-lg whitespace-nowrap transition-colors ${pathname === l.to ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}>{l.label}</Link>
          ))}
        </nav>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="font-display text-3xl" data-testid="legal-title">{title}</h1>
          {subtitle && <p className="text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <article className="prose-sm space-y-5 leading-relaxed">{children}</article>
        <LegalFooter className="pt-8 border-t border-border" />
      </main>
    </div>
  );
};

const H2 = ({ children }) => <h2 className="font-display text-xl mt-6 mb-2">{children}</h2>;
const P = ({ children }) => <p className="text-sm text-foreground/90">{children}</p>;
const UL = ({ items }) => <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>;

const Company = ({ info }) => {
  const c = info?.company || {};
  const rows = [
    ["Nom", c.name], ["Forme juridique", c.legal_form], ["Immatriculation", c.registration],
    ["Adresse", c.address], ["Pays", c.country], ["Site", c.website], ["Contact", c.contact_email],
  ].filter(([, v]) => v);
  return (
    <Card className="p-4 rounded-2xl text-sm" data-testid="company-block">
      <p className="font-medium mb-2">Éditeur du service</p>
      <dl className="grid grid-cols-[130px_1fr] gap-y-1">
        {rows.map(([k, v]) => (<React.Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd className="break-all">{v}</dd></React.Fragment>))}
      </dl>
    </Card>
  );
};

export function AboutPage() {
  const { info } = useInfo();
  return (
    <Shell title="À propos de MIHOD PAY" subtitle="Plateforme de vente de contenus numériques (photos et vidéos) entre créateurs et leurs abonnés.">
      <H2>Le service</H2>
      <P>MIHOD PAY est une application web et mobile qui permet à des créateurs de publier des photos et des vidéos, de les envoyer en privé à leurs fans, et de les vendre à l'unité. Les acheteurs paient par carte bancaire via Stripe ; le contenu payant n'est jamais visible avant la confirmation du paiement.</P>
      <H2>Produits et contenus vendus</H2>
      <UL items={[
        `Photos et vidéos numériques vendues à l'unité, prix fixé par le créateur entre ${info ? euro(info.photo_price_min) : "…"} et ${info ? euro(info.photo_price_max) : "…"}.`,
        "Abonnement à un créateur : accès à ses contenus privés pendant la durée choisie.",
        "Abonnement d'accès à l'application (après la période d'essai gratuite).",
        "Dépôt sur le portefeuille MIHOD PAY : solde prépayé utilisable uniquement pour acheter des contenus sur la plateforme (non convertible en espèces sauf remboursement d'un dépôt non utilisé).",
      ]} />
      <H2>Ce que le service n'est pas</H2>
      <P>MIHOD PAY n'est pas un établissement bancaire. Les paiements sont traités par Stripe ; aucune donnée de carte n'est stockée sur nos serveurs. Le solde du portefeuille provient exclusivement de paiements réellement confirmés par Stripe.</P>
      <H2>Public</H2>
      <P>Le service est réservé aux personnes majeures (18 ans et plus). Une pièce d'identité est demandée à l'inscription et vérifiée manuellement avant tout retrait de revenus.</P>
      {info && <Company info={info} />}
    </Shell>
  );
}

export function PricingPage() {
  const { info, error } = useInfo();
  return (
    <Shell title="Abonnements et tarifs" subtitle="Tous les prix sont en euros, TTC. Ils sont lus en temps réel depuis la configuration du service.">
      {!info && !error && <Skeleton className="h-40 rounded-2xl" />}
      {error && <P>Tarifs momentanément indisponibles.</P>}
      {info && (
        <>
          <H2>Contenus à l'unité</H2>
          <P>Chaque créateur fixe librement le prix de ses photos et vidéos, entre <strong>{euro(info.photo_price_min)}</strong> et <strong>{euro(info.photo_price_max)}</strong>. Répartition : <strong>{info.creator_share_percent} % pour le créateur</strong>, <strong>{info.platform_fee_percent} % pour MIHOD PAY</strong> (avant frais du prestataire de paiement).</P>
          <H2>Abonnement à un créateur</H2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="creator-tiers">
            {info.creator_subscription_tiers.map((t) => (
              <Card key={t.key} className="p-3 rounded-2xl text-center"><p className="text-xs text-muted-foreground">{t.label}</p><p className="font-display text-lg">{euro(t.price)}</p></Card>
            ))}
          </div>
          <P>Le créateur peut offrir un essai gratuit de {info.creator_trial_days} jours. Le montant des abonnements est intégralement perçu par MIHOD PAY ({info.subscription_platform_share_percent} %). Renouvellement automatique via Stripe, résiliable à tout moment depuis le profil ; l'accès reste actif jusqu'à la fin de la période payée.</P>
          <H2>Accès à l'application</H2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="app-plans">
            {info.app_plans.map((p) => (
              <Card key={p.key} className="p-3 rounded-2xl text-center"><p className="text-xs text-muted-foreground">{p.label}</p><p className="font-display text-lg">{p.price === 0 ? "0,00 €" : euro(p.price)}</p><p className="text-[11px] text-muted-foreground">{p.desc}</p></Card>
            ))}
          </div>
          <P>L'essai gratuit n'entraîne aucun prélèvement automatique : à son terme, l'utilisateur choisit librement une formule payante. Les formules payantes sont des paiements uniques pour la durée indiquée (pas de renouvellement automatique).</P>
          <H2>Dépôts et retraits</H2>
          <UL items={[
            `Dépôt sur le portefeuille : de ${euro(info.topup_min)} à ${euro(info.topup_max)} par opération, par carte, Apple Pay ou Google Pay.`,
            `Retrait des revenus créateurs : minimum ${euro(info.min_withdraw)}, via Stripe Connect vers le compte bancaire du créateur, après vérification d'identité.`,
            "Aucun frais supplémentaire n'est facturé par MIHOD PAY sur les dépôts.",
          ]} />
        </>
      )}
    </Shell>
  );
}

export function PaymentsPage() {
  const { info } = useInfo();
  return (
    <Shell title="Fonctionnement des paiements" subtitle="Paiements réels traités par Stripe. Aucun solde n'est crédité sans confirmation du paiement.">
      <div className="grid sm:grid-cols-3 gap-3">
        {[[CreditCard, "Paiement Stripe", "Carte, Apple Pay, Google Pay sur une page de paiement hébergée par Stripe."], [ShieldCheck, "Confirmation serveur", "Le paiement n'est considéré payé qu'après notification signée (webhook) de Stripe."], [Lock, "Contenu protégé", "La photo ou la vidéo n'est déverrouillée qu'après cette confirmation."]].map(([Icon, t, d]) => (
          <Card key={t} className="p-4 rounded-2xl"><Icon className="h-5 w-5 text-primary" /><p className="font-medium mt-2 text-sm">{t}</p><p className="text-xs text-muted-foreground mt-1">{d}</p></Card>
        ))}
      </div>
      <H2>Moyens de paiement acceptés</H2>
      <UL items={info ? info.payment_methods : ["Carte bancaire", "Apple Pay", "Google Pay"]} />
      <P>Prestataire de paiement : <strong>Stripe</strong>. Les données de carte sont saisies et conservées exclusivement par Stripe (certifié PCI-DSS). MIHOD PAY ne voit ni ne stocke jamais votre numéro de carte.</P>
      <H2>Étapes d'un achat</H2>
      <UL items={[
        "Vous choisissez un contenu, un abonnement ou un montant de dépôt.",
        "Vous êtes redirigé vers la page de paiement sécurisée Stripe.",
        "Stripe confirme le paiement à nos serveurs par un webhook signé. Un paiement seulement « commencé » ou déclaré réussi par le navigateur n'est jamais pris en compte.",
        "Le contenu est déverrouillé / l'abonnement activé / le portefeuille crédité du montant exact payé — une seule fois (protection contre les doubles crédits).",
        "Vous retrouvez chaque opération dans l'historique de votre portefeuille.",
      ]} />
      <H2>Répartition des revenus</H2>
      <UL items={[
        `Achat d'une photo ou vidéo : ${info ? info.creator_share_percent : 80} % pour le créateur, ${info ? info.platform_fee_percent : 20} % pour MIHOD PAY. Exemple : 12,00 € → 9,60 € créateur / 2,40 € plateforme (avant frais Stripe).`,
        "Abonnements (créateur et application) : 100 % perçus par MIHOD PAY. Exemple : 12,00 € → 12,00 € de revenu brut plateforme (avant frais Stripe).",
        "Cette répartition est calculée côté serveur et ne peut pas être modifiée depuis l'application.",
      ]} />
      <H2>Paiements échoués, annulés, remboursés</H2>
      <P>Un paiement échoué ou annulé n'entraîne aucun débit ni crédit. Un remboursement effectué via Stripe est répercuté automatiquement : le solde correspondant est retiré du portefeuille et l'opération apparaît dans l'historique.</P>
      <H2>Reçus</H2>
      <P>Stripe envoie un reçu par e-mail pour chaque paiement lorsque l'option est activée dans votre compte Stripe ; l'historique complet reste consultable dans l'application.</P>
    </Shell>
  );
}

export function RefundPage() {
  const { info } = useInfo();
  const mail = info?.company?.contact_email;
  return (
    <Shell title="Politique de remboursement" subtitle="Contenus numériques fournis immédiatement après paiement.">
      <H2>Contenus à l'unité (photos, vidéos)</H2>
      <P>Le contenu est livré et déverrouillé immédiatement après confirmation du paiement. Conformément à la réglementation sur les contenus numériques fournis immédiatement avec votre accord exprès, le droit de rétractation ne s'applique pas une fois le contenu déverrouillé. Un remboursement reste possible en cas de : contenu non livré à la suite d'une erreur technique, double facturation d'un même contenu, ou contenu ne correspondant manifestement pas à la description.</P>
      <H2>Dépôts sur le portefeuille</H2>
      <P>Un dépôt non utilisé peut être remboursé sur le moyen de paiement d'origine, sur simple demande, dans un délai de 14 jours suivant le dépôt. Le montant remboursé est retiré du solde du portefeuille.</P>
      <H2>Abonnements</H2>
      <P>Les abonnements peuvent être résiliés à tout moment ; l'accès reste actif jusqu'à la fin de la période déjà payée et aucun prélèvement ultérieur n'a lieu. Les périodes entamées ne sont pas remboursées, sauf indisponibilité prolongée du service imputable à MIHOD PAY.</P>
      <H2>Comment demander un remboursement</H2>
      <UL items={[
        `Écrivez-nous${mail ? ` à ${mail}` : " via la page Contact"} en indiquant l'e-mail de votre compte, la date et le montant du paiement.`,
        "Nous répondons sous 5 jours ouvrés. Les remboursements acceptés sont effectués via Stripe sur le moyen de paiement d'origine (délai bancaire habituel : 5 à 10 jours).",
        "Vous pouvez également contester un paiement auprès de votre banque ; nous coopérons avec Stripe pour toute procédure de litige.",
      ]} />
    </Shell>
  );
}

export function TermsPage() {
  const { info } = useInfo();
  return (
    <Shell title="Conditions générales d'utilisation" subtitle="En créant un compte, vous acceptez les présentes conditions.">
      <H2>1. Objet</H2>
      <P>MIHOD PAY met en relation des créateurs et des acheteurs pour la vente de contenus numériques (photos, vidéos) et d'abonnements. MIHOD PAY héberge les contenus, encaisse les paiements via Stripe et reverse aux créateurs leur part des ventes.</P>
      <H2>2. Accès au service</H2>
      <UL items={[
        "Le service est réservé aux personnes majeures. Une pièce d'identité est requise à l'inscription et vérifiée avant tout retrait de revenus.",
        "Un compte est personnel ; vous êtes responsable de la confidentialité de vos identifiants.",
        "L'accès complet à l'application peut nécessiter un abonnement après la période d'essai gratuite.",
      ]} />
      <H2>3. Contenus et obligations des créateurs</H2>
      <UL items={[
        "Vous ne publiez que des contenus dont vous détenez les droits et qui représentent des personnes majeures et consentantes.",
        "Sont interdits : contenus illégaux, violents, haineux, impliquant des mineurs, portant atteinte aux droits de tiers, ou contraires aux conditions d'utilisation de Stripe.",
        "MIHOD PAY peut retirer un contenu, suspendre ou clôturer un compte en cas de manquement, et coopère avec les autorités compétentes.",
      ]} />
      <H2>4. Prix, paiements et répartition</H2>
      <UL items={[
        `Les prix sont affichés en euros TTC. Les paiements sont traités par Stripe ; aucune donnée de carte n'est conservée par MIHOD PAY.`,
        `Vente de contenus à l'unité : ${info ? info.creator_share_percent : 80} % du prix pour le créateur, ${info ? info.platform_fee_percent : 20} % pour MIHOD PAY.`,
        "Abonnements (créateur et application) : intégralement perçus par MIHOD PAY.",
        "Un achat, un dépôt ou un abonnement n'est effectif qu'après confirmation du paiement par Stripe.",
        "Le solde du portefeuille n'est utilisable que sur la plateforme ; il ne produit pas d'intérêts et n'est pas un dépôt bancaire.",
      ]} />
      <H2>5. Retraits des créateurs</H2>
      <P>Les revenus disponibles peuvent être retirés à partir de {info ? euro(info.min_withdraw) : "15,00 €"} via Stripe Connect, après vérification d'identité (KYC) et ouverture d'un compte Stripe Express. Le créateur est seul responsable de ses obligations fiscales et sociales.</P>
      <H2>6. Licence et propriété intellectuelle</H2>
      <P>L'achat d'un contenu confère un droit d'accès personnel, privé et non transférable. Toute rediffusion, capture, revente ou partage est interdit. Les créateurs restent titulaires des droits sur leurs contenus et accordent à MIHOD PAY une licence d'hébergement et de diffusion nécessaire au service.</P>
      <H2>7. Responsabilité</H2>
      <P>MIHOD PAY fournit le service « en l'état » et met en œuvre des moyens raisonnables pour en assurer la disponibilité et la sécurité. MIHOD PAY n'est pas responsable des contenus publiés par les créateurs ni des dommages indirects.</P>
      <H2>8. Résiliation</H2>
      <P>Vous pouvez supprimer votre compte à tout moment depuis votre profil. MIHOD PAY peut suspendre un compte en cas de fraude, d'impayé ou de violation des présentes conditions.</P>
      <H2>9. Droit applicable</H2>
      <P>Les présentes conditions sont régies par le droit du pays d'établissement de l'éditeur indiqué sur la page À propos. En cas de litige, une solution amiable sera recherchée avant toute action judiciaire.</P>
      {info && <Company info={info} />}
    </Shell>
  );
}

export function PrivacyPage() {
  const { info } = useInfo();
  const mail = info?.company?.contact_email;
  return (
    <Shell title="Politique de confidentialité" subtitle="Quelles données nous traitons, pourquoi et combien de temps.">
      <H2>Données collectées</H2>
      <UL items={[
        "Compte : pseudo, adresse e-mail, mot de passe (haché), langue, avatar.",
        "Vérification d'âge / identité : image de la pièce d'identité fournie à l'inscription, consultée uniquement par l'équipe de modération.",
        "Contenus : photos, vidéos, messages, publications, interactions (likes, abonnements, favoris).",
        "Paiements : montants, dates, identifiants de transaction Stripe, historique du portefeuille. Les numéros de carte ne transitent jamais par nos serveurs (Stripe).",
        "Créateurs : informations de versement gérées par Stripe Connect (identité, compte bancaire) — non stockées par MIHOD PAY.",
        "Techniques : journaux de connexion et de sécurité.",
      ]} />
      <H2>Finalités et bases légales</H2>
      <UL items={[
        "Fourniture du service et exécution du contrat (compte, achats, abonnements, messagerie).",
        "Obligations légales : vérification de l'âge, lutte contre la fraude et le blanchiment, comptabilité.",
        "Sécurité et prévention des abus (intérêt légitime).",
        "Notifications liées à votre activité (ventes, achats, messages) — pas de prospection commerciale sans consentement.",
      ]} />
      <H2>Destinataires</H2>
      <UL items={[
        "Stripe (paiements, versements aux créateurs) — voir la politique de confidentialité de Stripe.",
        "Hébergeur de l'application et du stockage des médias.",
        "Autorités compétentes sur demande légale.",
      ]} />
      <H2>Durées de conservation</H2>
      <UL items={[
        "Compte et contenus : pendant la durée d'utilisation, puis suppression sous 30 jours après clôture.",
        "Données de paiement et comptabilité : 10 ans (obligation légale).",
        "Pièce d'identité : le temps de la vérification puis conservation limitée à la preuve de la vérification d'âge.",
      ]} />
      <H2>Vos droits</H2>
      <P>Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données, ainsi que du droit d'introduire une réclamation auprès de l'autorité de contrôle compétente. Pour exercer vos droits : {mail ? <a className="text-primary underline" href={`mailto:${mail}`}>{mail}</a> : "voir la page Contact"}.</P>
      <H2>Sécurité</H2>
      <P>Connexions chiffrées (HTTPS), mots de passe hachés, accès aux médias payants contrôlé par le serveur, secrets de paiement conservés exclusivement côté serveur.</P>
      <H2>Cookies</H2>
      <P>L'application utilise uniquement le stockage local du navigateur pour maintenir votre session. Aucun cookie publicitaire ni traceur tiers n'est utilisé.</P>
    </Shell>
  );
}

export function ContactPage() {
  const { info } = useInfo();
  const mail = info?.company?.contact_email;
  return (
    <Shell title="Contact" subtitle="Support, paiements, remboursements, signalements.">
      <Card className="p-5 rounded-2xl flex items-start gap-3" data-testid="contact-card">
        <Mail className="h-5 w-5 text-primary mt-0.5" />
        <div>
          <p className="font-medium">E-mail</p>
          {mail ? <a href={`mailto:${mail}`} className="text-primary underline break-all" data-testid="contact-email">{mail}</a> : <Skeleton className="h-5 w-48" />}
          <p className="text-xs text-muted-foreground mt-1">Réponse sous 5 jours ouvrés. Pour un paiement, indiquez l'e-mail du compte, la date et le montant.</p>
        </div>
      </Card>
      <H2>Signaler un contenu</H2>
      <P>Depuis l'application, chaque publication et chaque profil dispose d'un bouton « Signaler ». Les signalements sont examinés par l'équipe de modération.</P>
      {info && <Company info={info} />}
    </Shell>
  );
}
